import { type CategoryGridItem, iconForCategory } from '../lib/categoryGrid';
import { Icon } from './Icon';
import { NoImageFill } from './NoImageFill';

/**
 * Category tile grid (R12 field-fix F2 — "categories were read aloud, never shown").
 *
 * Field evidence (dev-73, 2026-07-08 turns 4–7): "show me the categories" → Gigi narrated all 12;
 * the showcase never changed. This renders the categories the server pushed (lib/categoryGrid.ts)
 * as editorial tiles so the visitor SEES them.
 *
 * Each tile: representative image (or an elegant type-only tile when no image), the display name,
 * and an ordinal badge (①②③…) consistent with the product-card deixis numbering, so Gigi's "the
 * third one" resolves the same for both parties. Tap → the existing `browsed-category` emission
 * (already live via onBrowseCategory) + a local advance (the parent clears the grid when the
 * server pushes that category's product cards).
 *
 * Design language: editorial tiles, hairline + serif, brass accent, no emoji. Tokens only,
 * day+night, reduced-motion safe (the .reveal stagger honours prefers-reduced-motion).
 */

const ORDINAL_GLYPHS = '①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳';
function ordinalGlyph(i: number): string {
  return ORDINAL_GLYPHS[i] ?? String(i + 1);
}

type Props = {
  items: CategoryGridItem[];
  /** The slug currently "finding…" after a tap (parent-owned loading affordance). */
  loadingSlug?: string | null;
  /** Tap → emit browsed-category + local advance. */
  onSelect: (slug: string) => void;
};

export function CategoryGrid({ items, loadingSlug, onSelect }: Props) {
  // R28 (5.3) → R29 X5 — a featured-first MOSAIC that fills full rows with NO orphan tile at either
  // breakpoint. The first (highest-traffic) tile is a full-WIDTH cinematic banner hero — `col-span-2`
  // on mobile, `col-span-3` on the ≥sm kiosk grid — and the remaining tiles flow beneath it as clean
  // rows. With the DB-true 13-slug CATEGORY_ORDER: hero(1 full row) + 12 singles = 4 clean 3-col rows
  // on kiosk (12 ÷ 3) and 6 clean 2-col rows on mobile (12 ÷ 2) — divisible at both, so no dangling
  // half-row. (The R28 2×2-square hero orphaned by one once the set grew from 12→13; the full-width
  // banner is orphan-proof for any tile count that leaves a 3-/2-divisible remainder.) `auto-rows-fr`
  // keeps cells even. Presentation only — deixis ordinals + tap emission unchanged.
  return (
    <div className="flex flex-col gap-5" data-testid="category-grid">
      <p className="text-sm text-subtle">
        Here's our range — tap a category and I'll show you the pieces.
      </p>
      <div className="grid auto-rows-fr grid-cols-2 gap-3 sm:grid-cols-3">
        {items.map((cat, i) => (
          <CategoryTile
            key={cat.slug}
            cat={cat}
            index={i}
            featured={i === 0}
            isLoading={loadingSlug === cat.slug}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * One category tile — R28 (5.1/5.2): a featured product photo under a warm-metallic DUOTONE wash
 * (`.tile-duotone`) with a SINGLE serif label bar at the foot (editorial), the deixis ordinal, and a
 * chevron. `.tile-lift` gives the pressable soft-UI (lift → sink on tap). Loading draws the brass
 * ring. A missing photo renders the branded `NoImageFill` weave (glyph only — the foot label carries
 * the name, so it's never doubled) — never a grey box. R29 X5: the featured tile is a full-WIDTH
 * cinematic banner (col-span-2 mobile / col-span-3 kiosk) with a wide letterbox aspect, so it anchors
 * the grid without orphaning the 13-tile set.
 */
function CategoryTile({
  cat,
  index,
  featured,
  isLoading,
  onSelect,
}: {
  cat: CategoryGridItem;
  index: number;
  featured: boolean;
  isLoading: boolean;
  onSelect: (slug: string) => void;
}) {
  const hasImage = !!cat.image;
  return (
    <button
      type="button"
      data-testid={`category-grid-tile-${cat.slug}`}
      onClick={() => onSelect(cat.slug)}
      aria-label={`Browse ${cat.name}`}
      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
      className={`tile-lift reveal group relative flex overflow-hidden rounded-xl bg-surface text-left ring-1 ${
        isLoading ? 'ring-2 ring-brass' : 'ring-line hover:ring-brass/50'
      } ${featured ? 'col-span-2 sm:col-span-3' : ''}`}
    >
      {/* Media well — the photo (or branded weave) fills the tile; one label bar sits at the foot. */}
      <div className="relative h-full w-full overflow-hidden">
        {hasImage ? (
          <img
            src={cat.image}
            alt=""
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-3 ease-physical group-hover:scale-[1.04]"
          />
        ) : (
          // No photo → the branded weave with the glyph ONLY (name is carried by the foot bar below,
          // so it's never printed twice). Sized to fill the tile.
          <div className="absolute inset-0">
            <NoImageFill icon={iconForCategory(cat.slug)} variant="tile" />
          </div>
        )}
        {/* Warm-metallic duotone wash — only over a real photo (the weave carries its own tint). */}
        {hasImage && (
          <span aria-hidden className="tile-duotone pointer-events-none absolute inset-0" />
        )}

        {/* Aspect spacer — gives the tile its frame height without an image driving it. Featured =
            a wide cinematic letterbox banner (kiosk); others a compact 4:3. R29 X5: the sm banner
            uses a 16:5 letterbox so the full-width hero stays a slim, elegant strip (not a wall). */}
        <div className={featured ? 'aspect-[16/7] sm:aspect-[16/5]' : 'aspect-[4/3]'} />

        {/* Deixis ordinal — matches the product-card numbering so "the third one" resolves alike. */}
        <span
          aria-hidden
          data-testid={`category-ordinal-${cat.slug}`}
          className="absolute top-2 left-2 z-[1] grid h-6 min-w-6 place-items-center rounded-full bg-canvas/70 px-1 text-sm leading-none text-brass ring-1 ring-brass/30 backdrop-blur-sm"
        >
          {ordinalGlyph(index)}
        </span>

        {/* The single serif label bar at the foot. Over a photo it rides the duotone's darkened foot;
            over the weave it sits on a hairline-topped surface strip so it stays legible in both
            themes. */}
        <div
          className={`absolute inset-x-0 bottom-0 z-[1] flex items-end justify-between gap-2 px-3 py-2.5 ${
            hasImage ? '' : 'border-t hairline bg-surface/80 backdrop-blur-sm'
          }`}
        >
          <span
            className={`font-display leading-tight ${hasImage ? 'text-white drop-shadow-sm' : 'text-ink'} ${
              featured ? 'text-lg' : 'text-sm'
            }`}
          >
            {isLoading ? 'Finding…' : cat.name}
          </span>
          <Icon
            name="chevronRight"
            size={featured ? 16 : 14}
            className={`mb-0.5 shrink-0 transition-colors duration-2 group-hover:text-brass ${
              hasImage ? 'text-white/80' : 'text-faint'
            }`}
          />
        </div>
      </div>
    </button>
  );
}
